// Localized storefront display.
import { tr, useStoreLanguage } from '@/i18n';

import React from 'react';

interface FreeShippingBarProps {
  subtotal: number;
  threshold: number;
}

/**
 * Displays a free‑shipping progress bar.
 * - Shows an unlocked state when `subtotal` meets or exceeds `threshold`.
 * - Shows a progress bar otherwise, safely handling edge‑cases such as a zero
 *   `threshold` to avoid division‑by‑zero errors.
 */
const FreeShippingBar: React.FC<FreeShippingBarProps> = ({
  subtotal,
  threshold,
}) => {
  useStoreLanguage();
  // Guard against a zero or negative threshold which would cause NaN/Infinity.
  const safeThreshold = Math.max(threshold, 0.01);
  const isUnlocked = subtotal >= safeThreshold;
  const progress = Math.min((subtotal / safeThreshold) * 100, 100);
  const remaining = safeThreshold - subtotal;

  const isAlmostThere = !isUnlocked && progress >= 70;

  return (
    <div className="mt-3 mb-1">
      {tr(isUnlocked ? (
        <div className="flex items-center gap-2 bg-forest-50 border border-forest-100 rounded-lg px-3 py-2.5">
          <div className="w-5 h-5 flex items-center justify-center bg-forest-500 rounded-full flex-shrink-0">
            <i className="ri-check-line text-white text-xs"></i>
          </div>
          <span className="text-forest-700 text-xs font-semibold">{tr("¡Envío gratis desbloqueado!")}</span>
          <div className="w-4 h-4 flex items-center justify-center ml-auto flex-shrink-0">
            <i className="ri-truck-line text-forest-600 text-sm"></i>
          </div>
        </div>
      ) : (
        <div className={`rounded-lg px-3 py-2.5 ${isAlmostThere ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50 border border-gray-100'}`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className={`text-xs font-medium ${isAlmostThere ? 'text-amber-800' : 'text-gray-600'}`}>
              {tr(isAlmostThere ? '¡Casi! Te faltan ' : 'Te faltan ')}
              <strong className={isAlmostThere ? 'text-amber-700' : 'text-forest-700'}>
                {tr(remaining.toFixed(2).replace('.', ','))} €
              </strong>
              {tr(' ')}{tr("para envío gratis")}</span>
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className={`ri-truck-line text-sm ${isAlmostThere ? 'text-amber-600' : 'text-gray-400'}`}></i>
            </div>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ease-out ${isAlmostThere ? 'bg-gradient-to-r from-amber-400 to-amber-500' : 'bg-gradient-to-r from-forest-400 to-forest-600'}`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-[10px] text-gray-400">0 €</span>
            <span className="text-[10px] text-gray-400">{tr(safeThreshold)} €</span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default FreeShippingBar;
